/* eslint-disable */
import React, { useState, useEffect } from 'react';
import * as yup from 'yup';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import RHFSelect from 'src/components/elements/Select/RHFSelect';
import RHFEditor from 'src/components/elements/Editor/RHFEditor';
import { Button } from '@mui/material';
import {
  CurrentInterviewsListValues,
  UpdateInterviewStatus,
} from '../../types';
import RHFDatePicker from 'src/components/elements/CustomDatePicker/RHFDatePicker';
import RHFInput from 'src/components/elements/Input/RHFInput';
import RHFEmailsChips from 'src/components/elements/EmailsChips/RHFEmailsChips';
import { useStatuses } from 'src/hooks/query/statuses';
import { Loader } from 'src/components';
import API from 'src/network';
import { confirmWrapper } from 'src/components/Dialog'
import { DialogContentProps } from 'src/components/Dialog/Dialog';
import useYupValidationResolver from 'src/hooks/useYupValidationResolver';
import { scrollToError } from 'src/utils/FormUtils';
import { MailTemplateRequest } from '../../types';

interface CurrentInterviewsActionsProps extends DialogContentProps {
  defaultValues?: CurrentInterviewsListValues
  onSave?: (data: UpdateInterviewStatus, close: (() => void) | undefined) => void;
  sendEmail?: (data: any, close: (() => void) | undefined) => void;
  recruiterId?: number | string;
}

export interface CurrentInterviewsActionFormValues {
  action: string;
  status: string;
  comments: string;
  date: string;
  duration: string;
  interviewMode: string;
  location: string;
  interviewer: string;
  emails: any;
  subject: string;
  content: string;
  hashKey: string;
  timeZone: string;
  interviewTime: string;
}

const CurrentInterviewsActions = function CurrentInterviewsActions(props: CurrentInterviewsActionsProps) {
  const {
    close,
    defaultValues,
    onSave,
    sendEmail,
  } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);

  const validationSchema = yup.object().shape({
    status: yup.string()
      .when(['action'], (values, schema) => {
        const selectedAction = values[0];
        return selectedAction === 'feedback'
          ? schema.required('Status is required')
          : schema.notRequired();
      }),
    comments: yup.string()
      .when(['action'], (values, schema) => {
        const selectedAction = values[0];
        return selectedAction === 'feedback'
          ? schema.required('Comments are required')
          : schema.notRequired();
      }),
    // emails: yup.array()
    //   .when(['action'], (values, schema) => {
    //     const selectedAction = values[0];
    //     return ['cancel', 'reminder'].includes(selectedAction)
    //       ? schema.min(1, 'At least one email is required').of(yup.string().email().required('Email is required'))
    //       : schema.of(yup.string().email());
    //   }),
    subject: yup.string()
      .when(['action'], (values, schema) => {
        const selectedAction = values[0];
        return ['cancel', 'reminder'].includes(selectedAction)
          ? schema.required('Subject is required')
          : schema.notRequired();
      }),
    content: yup.string()
      .when(['action'], (values, schema) => {
        const selectedAction = values[0];
        return ['cancel', 'reminder'].includes(selectedAction)
          ? schema.required('Content is required')
          : schema.notRequired();
      }),
  });

  const resolver = useYupValidationResolver(validationSchema);
  const methods = useForm<CurrentInterviewsActionFormValues>({
    resolver,
  });

  const {
    statusesLoading,
    statusesOptions,
    fetchStatuses,
  } = useStatuses();

  const {
    watch,
    handleSubmit,
    reset,
  } = methods;

  const onError = () => {
    scrollToError('current-interviews-action');
  };

  const actionValue = watch('action');

  const getMailTemplate = async (request: MailTemplateRequest) => {
    setLoading(true);
    try {
      const mailTemplate = await API.getInstance().getMailTemplate(request);
      const {
        emailAddress,
      } = defaultValues || {};
      reset({
        action: actionValue,
        status: '',
        comments: '',
        date: mailTemplate.interviewDate || '',
        duration: mailTemplate?.clientDurationTime || '',
        interviewMode: mailTemplate.clientInterviewMode || '',
        location: mailTemplate.clientInterviewLocation || '',
        interviewer: mailTemplate.clientInterviewerName || '',
        emails: emailAddress ? [emailAddress] : [],
        subject: mailTemplate.mailSubject || '',
        content: mailTemplate.mailContent || '',
        hashKey: mailTemplate.hashKey || '',
        timeZone: mailTemplate.timeZone || '',
        interviewTime: mailTemplate.interviewTime || '',
      })
    } catch (e) {
      await confirmWrapper('Something went wrong', {
        hideSecondaryBtn: true,
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (actionValue && (actionValue === 'cancel' || actionValue === 'reminder')) {
      const {
        jobOrderId,
        candidateId,
        interviewId,
      } = defaultValues || {};

      if (actionValue === 'cancel') {
        getMailTemplate({
          jobOrderId: Number(jobOrderId),
          candidateId: Number(candidateId),
          mailTemplate: 'CANCEL_INTERVIEW',
          interviewId: Number(interviewId),
        });
      } else if (actionValue === 'reminder') {
        getMailTemplate({
          jobOrderId: Number(jobOrderId),
          candidateId: Number(candidateId),
          mailTemplate: 'REMINDER_INTERVIEW',
          interviewId: Number(interviewId),
        });
      }
    }
  }, [actionValue])

  const submitHandler: SubmitHandler<CurrentInterviewsActionFormValues> = async (data) => {
    setDisableSubmit(true);
    const {
      interviewId,
      jobOrderId,
      candidateId,
    } = defaultValues || {};
    const {
      status,
      comments,
      date,
      duration,
      interviewMode,
      location,
      interviewer,
      emails,
      subject,
      content,
      hashKey,
      timeZone,
      interviewTime,
    } = data;

    if (actionValue === 'feedback') {
      const request = {
        jobOrderId: Number(jobOrderId),
        candidateId: Number(candidateId),
        interviewId: Number(interviewId),
        candidateStatus: status,
        comments,
      };
      if (onSave) {
        await onSave(request, close);
      }
    } else if (['cancel', 'reminder'].includes(actionValue)) {
      let mailTemplate;
      if (actionValue === 'cancel') {
        mailTemplate = 'CANCEL_INTERVIEW';
      } else {
        mailTemplate = 'REMINDER_INTERVIEW';
      }
  
      const request = {
        mailSubject: subject,
        mailBody: content,
        emailTagsList: emails,
        candidateId,
        jobOrderId,
        mailTemplate,
        hashKey,
        how: interviewMode,
        date,
        time: interviewTime,
        timeZone,
        duration,
        who: interviewer,
        where: location,
      };

      if (sendEmail) {
        await sendEmail(request, close);
      }
    }
    setDisableSubmit(false);
  };

  return (
    <div id="current-interviews-action">
      <FormProvider {...methods}>
        <form>
          <div className="flex justify-center items-center w-full h-[60px] py-10 text-[#28364C] font-medium text-base pl-[13px]">
            Actions on interview
          </div>
          <div className="bg-[#FAFAFA]">
            <div className="w-full flex items-center justify-start flex-col px-[10%] py-[1%]">
              <RHFSelect
                control={methods.control}
                name="action"
                label="Select the action required"
                options={[
                  { value: 'cancel', label: 'Cancel' },
                  { value: 'reminder', label: 'Reminder' },
                  { value: 'feedback', label: 'Feedback' },
                ]}
              />
            </div>
            {methods.watch('action') === 'feedback' && (
              <>
                <div className="w-full flex items-center justify-start flex-col px-[10%] py-[3%]">
                  <RHFSelect
                    control={methods.control}
                    name="status"
                    label="Status"
                    options={statusesOptions}
                  />
                </div>
                <div className="items-start w-full flex justify-start flex-col px-[10%] py-[1%]">
                  <div className="items-start w-full text-[#28364C] font-semibold text-base">Comments</div>
                  <RHFEditor
                    editorType="classic"
                    name="comments"
                    control={methods.control}
                    disabled={false}
                    height={250}
                  />
                </div>
                <div className="w-full justify-start flex items-start flex-col px-[10%] py-[3%]">
                  <Button variant="outlined" onClick={handleSubmit(submitHandler, onError)} className="font-medium rounded-full" size="medium" disabled={disableSubmit}>SUBMIT</Button>
                </div>
              </>
            )}
            {
              (!loading)
              && (
                methods.watch('action') === 'cancel' || methods.watch('action') === 'reminder') && (
                <>
                  <div className="flex items-start w-full gap-7 justify-start px-[10%] py-[3%]">
                    <div className="w-[250px]">
                      <RHFInput disabled control={methods.control} name='interviewMode' label='How' placeholder='Interview mode' />
                    </div>
                    <div className="w-[190px]">
                      <RHFDatePicker disabled control={methods.control} name='date' label='When' />
                    </div>
                    <div className="w-[190px]">
                      <RHFInput disabled control={methods.control} name='duration' label='Duration' />
                    </div>
                  </div>
                  <div className="flex items-start w-full gap-7 justify-start px-[10%] py-[1%]">
                    <div className="w-[250px]">
                      <RHFInput disabled control={methods.control} name='location' label='Where' placeholder='Enter interviewer location' />
                    </div>
                    <div className="w-[250px]">
                      <RHFInput disabled control={methods.control} name='interviewer' label='Who' placeholder='Enter interviewer name' />
                    </div>
                  </div>
                  <div className="bg-white flex flex-col px-[3%] py-[3%] border-1 border-[#c3c3c3] border-solid rounded-2xl" style={{ marginLeft: '10%', marginRight: '10%', marginTop: '4%' }}>
                    <div className="w-full flex-shrink-0">
                      <RHFEmailsChips
                        control={methods.control}
                        name="emails"
                        label="To:"
                        placeholder="Emails"
                      />
                    </div>
                    <div className="w-full flex-shrink-0 pt-5">
                      <RHFInput
                        control={methods.control}
                        name="subject"
                        label="Subject:"
                        placeholder="Subject"
                      />
                    </div>
                    <div className="w-full flex-shrink-0">
                      <RHFEditor
                        control={methods.control}
                        name="content"
                        height={400}
                      />
                    </div>
                  </div>
                  <div className="w-full justify-start py-[2%] px-[10%]">
                    <Button variant="outlined" onClick={handleSubmit(submitHandler, onError)} className="font-medium rounded-full" size="medium" disabled={disableSubmit}>SEND UPDATE</Button>
                  </div>
                </>
              )
            }
            {loading && (
              <div className="absolute top-[40%] left-[40%]">
                <Loader />
              </div>
            )}
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

CurrentInterviewsActions.defaultProps = {

};

CurrentInterviewsActions.propTypes = {

};

export default CurrentInterviewsActions;
